import type { ReactNode } from 'react';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ShellIndexRow } from '@/components/layout/shell/shell-index-row';

export interface ExtensionFilterChip {
  id: string;
  label: string;
  testId?: string;
  title?: string;
}

export interface ExtensionFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  chips?: ExtensionFilterChip[];
  activeChipId?: string;
  onChipChange?: (id: string) => void;
  /** `segment` = MaaterSegmentControl track (in-panel type filters). */
  chipStyle?: 'buttons' | 'segment';
  trailing?: ReactNode;
  secondaryRow?: ReactNode;
  countLabel?: ReactNode;
  className?: string;
  searchTestId?: string;
  chipsTestId?: string;
  testId?: string;
  /** Folder-style indexes hide this and use Cmd+F `PanelSearchBar` instead. */
  showSearch?: boolean;
  /** One ShellIndexRow line, aligned with the peek header. */
  singleRow?: boolean;
}

export function ExtensionFilterBar({
  search,
  onSearchChange,
  searchPlaceholder = 'Filter...',
  chips,
  activeChipId,
  onChipChange,
  chipStyle = 'buttons',
  trailing,
  secondaryRow,
  countLabel,
  className,
  searchTestId,
  chipsTestId,
  testId,
  showSearch = true,
  singleRow = false,
}: ExtensionFilterBarProps) {
  const content = (
    <>
      <div
        className={cn(
          'flex items-center gap-2',
          singleRow ? 'min-w-0 flex-1 flex-nowrap overflow-x-auto' : 'flex-wrap',
        )}
      >
        {chips?.length && chipStyle === 'segment' ? (
          <div
            className="inline-flex flex-wrap gap-1 rounded-[10px] bg-muted p-[3px]"
            role="tablist"
            aria-label="Filter"
            data-testid={chipsTestId}
          >
            {chips.map((chip) => {
              const active = activeChipId === chip.id
              return (
                <button
                  key={chip.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={chip.testId}
                  title={chip.title}
                  onClick={() => onChipChange?.(chip.id)}
                  className={cn(
                    'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                    active
                      ? 'border-primary bg-background text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}
                >
                  {chip.label}
                </button>
              )
            })}
          </div>
        ) : null}
        {chips?.length && chipStyle === 'buttons'
          ? chips.map((chip) => (
              <Button
                key={chip.id}
                size="sm"
                variant={activeChipId === chip.id ? 'secondary' : 'ghost'}
                className="h-7 text-xs"
                onClick={() => onChipChange?.(chip.id)}
              >
                {chip.label}
              </Button>
            ))
          : null}
        {showSearch ? (
          <div className="relative min-w-[12rem] flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-7 pl-7 text-xs"
              aria-label={searchPlaceholder}
              data-testid={searchTestId}
            />
          </div>
        ) : null}
        {trailing}
      </div>
      {(secondaryRow || countLabel) && (
        <div className="flex flex-wrap items-center gap-2">
          {secondaryRow}
          {countLabel ? (
            <span className="text-[11px] text-muted-foreground">{countLabel}</span>
          ) : null}
        </div>
      )}
    </>
  );

  if (singleRow) {
    return (
      <ShellIndexRow
        testId={testId}
        className={cn('gap-0 overflow-hidden bg-background', className)}
      >
        {content}
      </ShellIndexRow>
    );
  }

  return (
    <div data-testid={testId} className={cn('space-y-2', className)}>
      {content}
    </div>
  );
}
